// Dramatic answer reveal: correct answer slam-in + per-scorer point chips
// sliding from the left. Per-player NAMES are intentionally NOT rendered here
// — chat can be busy and shouting names mid-stage feels noisy. The side
// leaderboard names+scores everyone in a quieter, persistent place.

import {
  Mesh,
  MeshBasicMaterial,
  Object3D,
  PlaneGeometry,
  type Texture,
} from 'three';
import type { ScorerRow } from '@sg/shared';
import { clamp01, easeOutBack, easeOutCubic } from './lib/easing.js';
import { textTexture } from './lib/text-texture.js';
import {
  COLOR_GOLD,
  COLOR_STAGE_BLUE,
  FONT_DISPLAY,
  FONT_SLAB,
  FONT_UI,
  STAGE_HEIGHT,
  STAGE_WIDTH,
} from './lib/theme.js';

const CARD_W = 1300;
const CARD_H = 320;
const ROW_W = 800;
const ROW_H = 88;

export interface AnswerRevealHandle {
  group: Object3D;
  show: (displayAnswer: string, scorers: ScorerRow[]) => void;
  hide: () => void;
  tick: (nowMs: number) => void;
  dispose: () => void;
}

interface ScorerRowVis {
  card: Mesh;
  nameMesh: Mesh;
}

export function createAnswerReveal(): AnswerRevealHandle {
  const group = new Object3D();
  group.name = 'answer-reveal';
  group.visible = false;

  const scrim = new Mesh(
    new PlaneGeometry(STAGE_WIDTH, STAGE_HEIGHT),
    new MeshBasicMaterial({ color: 0x000000, transparent: true, opacity: 0 }),
  );
  scrim.position.z = -10;
  scrim.renderOrder = 10;
  group.add(scrim);

  const cardGeom = new PlaneGeometry(CARD_W, CARD_H);
  const cardMat = new MeshBasicMaterial({ color: COLOR_STAGE_BLUE, transparent: true });
  const card = new Mesh(cardGeom, cardMat);
  card.position.set(0, 180, 0);
  card.renderOrder = 20;
  group.add(card);

  const answerMat = new MeshBasicMaterial({ transparent: true, depthWrite: false });
  const answer = new Mesh(new PlaneGeometry(CARD_W - 60, CARD_H - 60), answerMat);
  answer.position.set(0, 180, 0.1);
  answer.renderOrder = 30;
  group.add(answer);

  const rows: ScorerRowVis[] = [];
  let startMs: number | null = null;

  function clearRows(): void {
    for (const r of rows) {
      (r.card.material as MeshBasicMaterial).dispose();
      r.card.geometry.dispose();
      (r.nameMesh.material as MeshBasicMaterial).dispose();
      r.nameMesh.geometry.dispose();
      group.remove(r.card);
      group.remove(r.nameMesh);
    }
    rows.length = 0;
  }

  function buildRow(i: number, sc: ScorerRow): ScorerRowVis {
    const y = -80 - i * (ROW_H + 12);
    const cg = new PlaneGeometry(ROW_W, ROW_H);
    const cm = new MeshBasicMaterial({ color: 0x0b1033, transparent: true });
    const c = new Mesh(cg, cm);
    c.position.set(-STAGE_WIDTH / 2 - ROW_W, y, 0);
    c.renderOrder = 20;
    group.add(c);

    // Anonymous: just "+points" — the side leaderboard handles names.
    const nameTex: Texture = textTexture(`+${sc.points}`, {
      font: `40px ${FONT_UI}`,
      color: '#FFFFFF',
      width: ROW_W - 40,
      height: ROW_H - 20,
      align: 'left',
      paddingX: 28,
    });
    const nm = new MeshBasicMaterial({ transparent: true, map: nameTex });
    const nameMesh = new Mesh(new PlaneGeometry(ROW_W - 40, ROW_H - 20), nm);
    nameMesh.position.set(-STAGE_WIDTH / 2 - ROW_W, y, 0.1);
    nameMesh.renderOrder = 21;
    group.add(nameMesh);

    return { card: c, nameMesh };
  }

  function show(displayAnswer: string, scorers: ScorerRow[]): void {
    group.visible = true;
    startMs = performance.now();
    const tex: Texture = textTexture(displayAnswer.toUpperCase(), {
      font: `140px ${FONT_DISPLAY}`,
      color: '#FFC43D',
      width: CARD_W - 60,
      height: CARD_H - 60,
      weight: 'bold',
      stroke: '#000000',
      strokeWidth: 5,
    });
    if (answerMat.map && answerMat.map !== tex) answerMat.map.dispose();
    answerMat.map = tex;
    answerMat.needsUpdate = true;
    answerMat.opacity = 0;

    clearRows();
    scorers.slice(0, 5).forEach((sc, i) => rows.push(buildRow(i, sc)));
    void COLOR_GOLD;
    void FONT_SLAB;
  }

  function hide(): void {
    group.visible = false;
    startMs = null;
    clearRows();
  }

  function tick(nowMs: number): void {
    if (!group.visible || startMs === null) return;
    const elapsed = nowMs - startMs;
    const t1 = clamp01(elapsed / 600);
    const e1 = easeOutBack(t1);
    card.scale.setScalar(0.5 + 0.5 * e1);
    answerMat.opacity = clamp01((elapsed - 300) / 400);
    (scrim.material as MeshBasicMaterial).opacity = 0.6 * t1;

    rows.forEach((r, i) => {
      const t = clamp01((elapsed - 500 - i * 120) / 500);
      const e = easeOutCubic(t);
      const targetX = -STAGE_WIDTH / 2 + ROW_W / 2 + 40;
      const startX = -STAGE_WIDTH / 2 - ROW_W;
      const x = startX + (targetX - startX) * e;
      r.card.position.x = x;
      r.nameMesh.position.x = x;
    });
  }

  return {
    group,
    show,
    hide,
    tick,
    dispose() {
      cardGeom.dispose();
      cardMat.dispose();
      answerMat.dispose();
      answer.geometry.dispose();
      scrim.geometry.dispose();
      (scrim.material as MeshBasicMaterial).dispose();
      clearRows();
    },
  };
}
